// Propósito: página pública del chatbot con acceso y conversación.
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AccessForm } from '../../components/public/AccessForm';
import { OtpVerification } from '../../components/public/OtpVerification';
import { BotSelector } from '../../components/public/BotSelector';
import { ChatWindow } from '../../components/public/ChatWindow';
import { chatbotPublicApi } from '../../api/chatbotApi';
import { Spinner } from '@/components/ui/spinner';
import { showToast } from '@/components/ui/toast.utils';
import { clearStoredAuthSession, getStoredAuthToken } from '@/lib/httpClient';
import { getOrCreateAnonymousId } from '@/features/chatbot/utils/anonymousSession';
import {
  getTabActiveConversationId,
  setTabActiveConversationId,
} from '@/features/chatbot/utils/chatbotTabSession';
import { readAndClearLinkToken } from '@/features/chatbot/utils/linkToken';
import { captureLeadAttribution } from '@/features/chatbot/utils/leadAttribution';
import type {
  ChatbotBotPublic,
  ChatbotMessage,
  ChatbotAccessType,
  ChatbotVerificationMethod,
  ChatbotInputConfig,
  ChatbotLimitsConfig,
  ChatbotConversation,
  ChatbotContentType,
  FlowiseUpload,
  ChatbotLimitState,
  ChatbotTransferredTo,
} from '../../types/chatbot.types';

type ChatStep = 'loading' | 'access' | 'otp' | 'bots' | 'chat' | 'error';
type AccessMode = 'email' | 'phone' | 'anonymous';

const MESSAGES_PAGE_SIZE = 50;

interface InstanceConfig {
  accessType: ChatbotAccessType;
  verificationMethod: ChatbotVerificationMethod;
  allowedDomains: string[];
  instanceName: string;
  inputConfig?: ChatbotInputConfig;
  limitsConfig?: ChatbotLimitsConfig;
  webMandatory?: boolean;
}

export function ChatbotPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<ChatStep>('loading');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Instance config
  const [instanceConfig, setInstanceConfig] = useState<InstanceConfig | null>(null);

  // Auth state
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState<string | undefined>();
  const [accessMode, setAccessMode] = useState<AccessMode>('email');
  const [otpIdentifierType, setOtpIdentifierType] = useState<'EMAIL' | 'PHONE'>('EMAIL');

  // Bots & Chat state
  const [bots, setBots] = useState<ChatbotBotPublic[]>([]);
  const [selectedBot, setSelectedBot] = useState<ChatbotBotPublic | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatbotMessage[]>([]);
  const [messagesOffset, setMessagesOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [conversations, setConversations] = useState<ChatbotConversation[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [limitState, setLimitState] = useState<ChatbotLimitState | undefined>(undefined);
  const [failedMessageContent, setFailedMessageContent] = useState<string | undefined>(undefined);
  const [stickyLabel, setStickyLabel] = useState<string | null>(null);
  const [leadAttribution] = useState(() => captureLeadAttribution(instanceId));
  // Fetch instance info on mount
  useEffect(() => {
    if (!instanceId) {
      setStep('error');
      setError('ID de instancia no proporcionado');
      return;
    }

    // Try to load bots - if successful, user is already authenticated
    // Otherwise, we need to show access form
    checkAuthAndLoadBots();
  }, [instanceId]);

  useEffect(() => {
    if (step !== 'chat' || !instanceId || !conversationId || isSending) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      void chatbotPublicApi
        .listMessages(instanceId, conversationId, MESSAGES_PAGE_SIZE, 0, 'desc')
        .then(data => {
          setMessages([...data].reverse());
          setMessagesOffset(data.length);
          setHasMoreMessages(data.length === MESSAGES_PAGE_SIZE);
        })
        .catch(() => undefined);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [step, instanceId, conversationId, isSending]);

  const checkAuthAndLoadBots = async () => {
    if (!instanceId) return;

    const linkToken = readAndClearLinkToken();
    if (linkToken) {
      try {
        setStep('loading');
        await fetchInstanceConfig(false);
        const { botId } = await chatbotPublicApi.redeemLink(instanceId, linkToken);
        const botsData = await chatbotPublicApi.listBots(instanceId);
        setBots(botsData);
        const bot = botsData.find(item => item.id === botId) ?? botsData[0];
        if (bot) {
          await openChat(bot);
          return;
        }
      } catch {
        showToast('El enlace venció. Ingresá con tu email.', 'error', 5000);
      }
    }

    try {
      setStep('loading');
      await fetchInstanceConfig(false);

      if (getStoredAuthToken()) {
        try {
          await chatbotPublicApi.createPlatformSession(instanceId);
        } catch {
          // SSO de plataforma no aplica (otro tenant / sin permiso): seguir con flujo público.
        }
      }

      const botsData = await chatbotPublicApi.listBots(instanceId);
      await handleBotsLoaded(botsData);
    } catch {
      try {
        await fetchInstanceConfig(true);
      } catch (error_: unknown) {
        setStep('error');
        const message =
          error_ instanceof Error ? error_.message : 'No se pudo cargar la configuración';
        setError(`Error de configuración: ${message}`);
      }
    }
  };

  const fetchInstanceConfig = async (shouldSetAccessStep: boolean = true) => {
    if (!instanceId) return;

    try {
      const config = await chatbotPublicApi.getPublicConfig(instanceId);
      setInstanceConfig({
        accessType: config.accessType,
        verificationMethod: config.verificationMethod,
        allowedDomains: config.allowedDomains,
        instanceName: config.name,
        inputConfig: config.inputConfig,
        limitsConfig: config.limitsConfig,
        webMandatory: config.concierge?.webMandatory,
      });

      if (!shouldSetAccessStep) {
        return;
      }

      if (getStoredAuthToken()) {
        try {
          await chatbotPublicApi.createPlatformSession(instanceId);
          await loadBots();
          return;
        } catch {
          // Continuar con acceso anónimo / formulario público.
        }
      }

      if (
        config.accessType === 'ANONYMOUS' ||
        (config.accessType === 'OPEN' && config.verificationMethod === 'NONE')
      ) {
        setAccessMode('anonymous');
        try {
          const anonymousId = getOrCreateAnonymousId();
          await chatbotPublicApi.anonymousAccess(instanceId, anonymousId);
          await loadBots();
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Error al acceder';
          setError(message);
          setStep('error');
        }
        return;
      }

      if (config.accessType === 'OPEN' && config.verificationMethod === 'SMS_OTP') {
        setAccessMode('phone');
      } else {
        setAccessMode('email');
      }
      setStep('access');
    } catch {
      setStep('error');
      setError('No se pudo cargar la configuración del chatbot');
    }
  };

  const handleAccessSubmit = async ({
    email: emailValue,
    phone: phoneValue,
  }: {
    email?: string;
    phone?: string;
  }) => {
    if (!instanceId) return;

    setIsLoading(true);
    setError(null);
    if (emailValue) setEmail(emailValue);
    if (phoneValue) setPhone(phoneValue);

    try {
      if (accessMode === 'anonymous') {
        const anonymousId = getOrCreateAnonymousId();
        await chatbotPublicApi.anonymousAccess(instanceId, anonymousId);
        await loadBots();
        return;
      }

      const payload = {
        email: emailValue || email || undefined,
        phone: phoneValue || phone || undefined,
      };

      const response = await chatbotPublicApi.requestOtp(instanceId, payload);
      if (response.phoneRequired) {
        setAccessMode('phone');
        return;
      }
      if (response.otpRequired) {
        setOtpExpiresAt(response.expiresAt);
        const shouldUsePhone = instanceConfig?.verificationMethod === 'SMS_OTP';
        setOtpIdentifierType(shouldUsePhone ? 'PHONE' : 'EMAIL');
        setStep('otp');
      } else {
        // No OTP required, direct access
        await loadBots();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al enviar código';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    if (!instanceId) return;

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        code,
        email: email || undefined,
        phone: phone || undefined,
      };
      await chatbotPublicApi.verifyOtp(instanceId, payload);
      await loadBots();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Código inválido';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!instanceId) return;

    setError(null);

    try {
      const response = await chatbotPublicApi.requestOtp(instanceId, {
        email: email || undefined,
        phone: phone || undefined,
      });
      setOtpExpiresAt(response.expiresAt);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al reenviar código';
      setError(message);
    }
  };

  const handleBackFromOtp = () => {
    setStep('access');
    setError(null);
  };

  const loadBots = async () => {
    if (!instanceId) return;

    try {
      const botsData = await chatbotPublicApi.listBots(instanceId);
      await handleBotsLoaded(botsData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al cargar asistentes';
      const isSessionError =
        message.includes('Sesión no válida') || message.includes('status code 401');
      if (isSessionError && instanceConfig) {
        await fetchInstanceConfig(true);
        return;
      }
      setError(message);
      setStep('error');
    }
  };

  const handleBotsLoaded = async (botsData: ChatbotBotPublic[]) => {
    setBots(botsData);
    if (botsData.length === 1) {
      await openChat(botsData[0]);
      return;
    }
    setStep('bots');
  };

  // Check limits when opening chat
  const checkLimits = async () => {
    if (!instanceId) return;
    try {
      const limitStatus = await chatbotPublicApi.checkLimits(instanceId);
      if (!limitStatus.allowed) {
        setLimitState({
          reached: true,
          reason: limitStatus.reason,
          resetAt: limitStatus.resetAt,
        });
      }
    } catch (err) {
      console.error('Error checking limits:', err);
    }
  };

  const loadMessagesPage = async (convId: string, offset: number, append: boolean) => {
    if (!instanceId) return;
    const messagesData = await chatbotPublicApi.listMessages(
      instanceId,
      convId,
      MESSAGES_PAGE_SIZE,
      offset,
      'desc'
    );
    const orderedMessages = [...messagesData].reverse();
    setMessages(prev => (append ? [...orderedMessages, ...prev] : orderedMessages));
    setMessagesOffset(offset + messagesData.length);
    setHasMoreMessages(messagesData.length === MESSAGES_PAGE_SIZE);
  };

  const openChat = async (bot: ChatbotBotPublic) => {
    if (!instanceId) return;

    setSelectedBot(bot);
    setStickyLabel(null);
    setStep('chat');
    setIsLoadingMessages(true);

    // Check limits immediately upon entering
    checkLimits();

    try {
      const conversationsData = await chatbotPublicApi.listConversations(instanceId, bot.id);
      const storedConvId = getTabActiveConversationId(instanceId, bot.id);
      const canResumeStored =
        storedConvId && conversationsData.some(conv => conv.id === storedConvId);

      const conv = canResumeStored
        ? { id: storedConvId }
        : await chatbotPublicApi.createConversation(instanceId, bot.id);

      setTabActiveConversationId(instanceId, bot.id, conv.id);
      setConversationId(conv.id);
      setMessagesOffset(0);
      await loadMessagesPage(conv.id, 0, false);

      if (bot.conversationMode === 'MULTIPLE') {
        const refreshed = await chatbotPublicApi.listConversations(instanceId, bot.id);
        setConversations(refreshed);
      }
    } catch {
      try {
        const conv = await chatbotPublicApi.createConversation(instanceId, bot.id);
        setTabActiveConversationId(instanceId, bot.id, conv.id);
        setConversationId(conv.id);
        setMessagesOffset(0);
        await loadMessagesPage(conv.id, 0, false);
      } catch {
        setConversationId(null);
        setMessages([]);
        setMessagesOffset(0);
        setHasMoreMessages(false);
      }
      if (bot.conversationMode === 'MULTIPLE') {
        setConversations([]);
      }
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleSelectBot = async (botId: string) => {
    if (!instanceId) return;

    const bot = bots.find(b => b.id === botId);
    if (!bot) return;

    await openChat(bot);
  };

  const clearLimitAfterSuccessfulSend = (): void => {
    if (limitState?.reached) {
      setLimitState(undefined);
    }
  };

  const buildTemporaryUserMessage = (payload: {
    content: string;
    contentType: ChatbotContentType;
    fileUrl?: string;
    fileMetadata?: { name: string; size: number; mimeType: string; type?: string };
  }): ChatbotMessage => ({
    id: `tmp-user-${Date.now()}`,
    sender: 'USER',
    contentType: payload.contentType,
    content: payload.content,
    fileUrl: payload.fileUrl || null,
    fileMetadata: payload.fileMetadata || null,
    createdAt: new Date().toISOString(),
  });

  const refreshMultipleConversations = async (
    botId: string,
    mode: 'SINGLE' | 'MULTIPLE'
  ): Promise<void> => {
    if (!instanceId || mode !== 'MULTIPLE') {
      return;
    }
    const conversationsData = await chatbotPublicApi.listConversations(instanceId, botId);
    setConversations(conversationsData);
  };

  const ensureConversationForSend = async (): Promise<string | null> => {
    if (!instanceId || !selectedBot) return null;
    if (conversationId) return conversationId;

    const conv = await chatbotPublicApi.createConversation(instanceId, selectedBot.id);
    setTabActiveConversationId(instanceId, selectedBot.id, conv.id);
    setConversationId(conv.id);
    await refreshMultipleConversations(selectedBot.id, selectedBot.conversationMode);
    return conv.id;
  };

  const applyRateLimitFromError = (errorData: unknown): boolean => {
    if (!errorData || typeof errorData !== 'object') return false;

    const parsed = errorData as { error?: string; resetAt?: string };
    const errorMessage = parsed.error || '';
    let reason: 'hourly' | 'daily' | 'total' = 'hourly';
    if (errorMessage.includes('día')) reason = 'daily';
    if (errorMessage.includes('total')) reason = 'total';

    setLimitState({
      reached: true,
      reason,
      resetAt: parsed.resetAt,
    });
    return true;
  };

  const sendMessageWithStreaming = async (
    activeConversationId: string,
    payload: {
      content: string;
      contentType: ChatbotContentType;
      uploads?: FlowiseUpload[];
      fileUrl?: string;
      fileMetadata?: { name: string; size: number; mimeType: string; type?: string };
    },
    userMessage: ChatbotMessage
  ): Promise<void> => {
    if (!instanceId) return;

    const streamingMessageId = `streaming-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      userMessage,
      {
        id: streamingMessageId,
        sender: 'BOT',
        contentType: 'TEXT',
        content: '',
        fileUrl: null,
        createdAt: new Date().toISOString(),
      },
    ]);

    await chatbotPublicApi.streamMessage(
      instanceId,
      activeConversationId,
      {
        content: payload.content,
        contentType: payload.contentType,
        uploads: payload.uploads,
        fileUrl: payload.fileUrl,
        fileMetadata: payload.fileMetadata || undefined,
        streaming: true,
        attribution: leadAttribution,
      },
      {
        onToken: token => {
          setMessages(prev =>
            prev.map(message =>
              message.id === streamingMessageId
                ? { ...message, content: `${message.content}${token}` }
                : message
            )
          );
        },
        onFinalMessage: botMessage => {
          setMessages(prev =>
            prev.map(message => (message.id === streamingMessageId ? botMessage : message)),
          );
          void chatbotPublicApi
            .listMessages(instanceId, activeConversationId, MESSAGES_PAGE_SIZE, 0, 'desc')
            .then(data => {
              setMessages([...data].reverse());
              setMessagesOffset(data.length);
              setHasMoreMessages(data.length === MESSAGES_PAGE_SIZE);
            })
            .catch(() => undefined);
          clearLimitAfterSuccessfulSend();
        },
        onError: _message => {
          showToast('Ups, algo salió mal. Por favor intentá de nuevo.', 'error', 5000);
          setMessages(prev => prev.filter(msg => msg.id !== streamingMessageId));
          setFailedMessageContent(payload.content);
        },
      }
    );
  };

  const applyTransferredTo = async (transferredTo: ChatbotTransferredTo) => {
    const conciergeBot = bots.find(bot => bot.kind === 'CONCIERGE');
    const specialist = bots.find(bot => bot.id === transferredTo.botId);
    const nextBot = specialist ?? {
      id: transferredTo.botId,
      name: transferredTo.botName,
      description: null,
      conversationMode: 'SINGLE' as const,
      iconUrl: selectedBot?.iconUrl ?? null,
      kind: transferredTo.botId === conciergeBot?.id ? ('CONCIERGE' as const) : ('STANDARD' as const),
    };
    setSelectedBot(nextBot);
    if (instanceId) {
      setTabActiveConversationId(instanceId, nextBot.id, transferredTo.conversationId);
    }
    setConversationId(transferredTo.conversationId);
    setStickyLabel(nextBot.kind === 'CONCIERGE' ? null : transferredTo.botName);
    await loadMessagesPage(transferredTo.conversationId, 0, false);
  };

  const sendMessageWithoutStreaming = async (
    activeConversationId: string,
    payload: {
      content: string;
      contentType: ChatbotContentType;
      uploads?: FlowiseUpload[];
      fileUrl?: string;
      fileMetadata?: { name: string; size: number; mimeType: string; type?: string };
    }
  ): Promise<void> => {
    if (!instanceId) return;

    const response = await chatbotPublicApi.sendMessage(instanceId, activeConversationId, {
      content: payload.content,
      contentType: payload.contentType,
      uploads: payload.uploads,
      fileUrl: payload.fileUrl,
      fileMetadata: payload.fileMetadata || undefined,
      attribution: leadAttribution,
    });

    if (response.transferredTo) {
      await applyTransferredTo(response.transferredTo);
      clearLimitAfterSuccessfulSend();
      return;
    }

    setMessages(prev => {
      const next = [...prev, response.userMessage];
      if (response.botMessage.content?.trim()) {
        next.push(response.botMessage);
      }
      return next;
    });
    setMessagesOffset(prev => prev + (response.botMessage.content?.trim() ? 2 : 1));
    clearLimitAfterSuccessfulSend();
  };

  const handleSendMessage = async (payload: {
    content: string;
    contentType: ChatbotContentType;
    uploads?: FlowiseUpload[];
    fileUrl?: string;
    fileMetadata?: { name: string; size: number; mimeType: string; type?: string };
  }) => {
    if (!instanceId || !selectedBot) return;

    setIsSending(true);
    try {
      const activeConversationId = await ensureConversationForSend();
      if (!activeConversationId) {
        setError('No se pudo crear la conversación');
        return;
      }

      const shouldStream =
        payload.contentType === 'TEXT' &&
        selectedBot.kind !== 'CONCIERGE' &&
        !stickyLabel;
      if (shouldStream) {
        const userMessage = buildTemporaryUserMessage(payload);
        await sendMessageWithStreaming(activeConversationId, payload, userMessage);
      } else {
        await sendMessageWithoutStreaming(activeConversationId, payload);
      }
    } catch (err: unknown) {
      console.error('Error sending message:', err);
      const withResponse = err as {
        response?: { status?: number; data?: unknown };
        status?: number;
        data?: unknown;
      };
      const status = withResponse.response?.status || withResponse.status;
      const errorData = withResponse.response?.data || withResponse.data;

      // Check for rate limit error (429) - no mostrar toast ni repoblar
      if (status === 429 && applyRateLimitFromError(errorData)) {
        return;
      }

      // Mostrar toast de error y repoblar input
      showToast('Ups, algo salió mal. Por favor intentá de nuevo.', 'error', 5000);
      setFailedMessageContent(payload.content);
    } finally {
      setIsSending(false);
    }
  };

  const handleUploadFile = async (payload: {
    file: File;
    contentType: 'audio' | 'image' | 'file';
  }) => {
    if (!instanceId || !selectedBot || !conversationId) {
      throw new Error('No instance or conversation available');
    }
    return await chatbotPublicApi.uploadFile(instanceId, {
      file: payload.file,
      contentType: payload.contentType,
      botId: selectedBot.id,
      conversationId,
    });
  };

  const handleSelectConversation = async (convId: string) => {
    if (!instanceId || !selectedBot) return;

    setTabActiveConversationId(instanceId, selectedBot.id, convId);
    setConversationId(convId);
    setIsLoadingMessages(true);
    setMessagesOffset(0);

    try {
      await loadMessagesPage(convId, 0, false);
    } catch (err: unknown) {
      console.error('Error loading conversation messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleLoadMoreMessages = async () => {
    if (!instanceId || !conversationId || isLoadingMoreMessages || !hasMoreMessages) return;
    setIsLoadingMoreMessages(true);
    try {
      await loadMessagesPage(conversationId, messagesOffset, true);
    } catch (err: unknown) {
      console.error('Error loading more messages:', err);
    } finally {
      setIsLoadingMoreMessages(false);
    }
  };

  const handleHideConversation = async (convId: string) => {
    if (!instanceId || !selectedBot) return;

    const accepted = window.confirm(
      '¿Ocultar esta conversación? No volverá a aparecer en tu lista.'
    );
    if (!accepted) return;

    try {
      await chatbotPublicApi.hideConversation(instanceId, convId);
      const remainingConversations = conversations.filter(conv => conv.id !== convId);
      setConversations(remainingConversations);

      if (conversationId !== convId) {
        return;
      }

      const nextConversation = remainingConversations[0];
      if (!nextConversation) {
        setConversationId(null);
        setMessages([]);
        setMessagesOffset(0);
        setHasMoreMessages(false);
        return;
      }

      setConversationId(nextConversation.id);
      setMessagesOffset(0);
      await loadMessagesPage(nextConversation.id, 0, false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al ocultar conversación';
      showToast(message, 'error', 5000);
    }
  };

  const handleNewConversation = async () => {
    if (!instanceId || !selectedBot) return;

    setIsLoadingMessages(true);
    setMessages([]);
    setMessagesOffset(0);
    setHasMoreMessages(false);

    try {
      const conversation = await chatbotPublicApi.createConversation(instanceId, selectedBot.id);
      setTabActiveConversationId(instanceId, selectedBot.id, conversation.id);
      setConversationId(conversation.id);

      // Refresh conversations list
      const conversationsData = await chatbotPublicApi.listConversations(
        instanceId,
        selectedBot.id
      );
      setConversations(conversationsData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear conversación';
      setError(message);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleBackFromChat = () => {
    if (instanceConfig?.webMandatory) {
      return;
    }
    setSelectedBot(null);
    setConversationId(null);
    setMessages([]);
    setStickyLabel(null);
    setStep('bots');
  };

  const handleLogout = async () => {
    if (!instanceId) return;

    setIsLoading(true);
    try {
      clearStoredAuthSession();
      try {
        await chatbotPublicApi.logout(instanceId);
      } catch {
        // El cierre local debe continuar aunque la cookie ya no exista o el endpoint falle.
      }

      navigate('/login', { replace: true });
    } catch {
      showToast('Se cerró la sesión local, pero no se pudo volver al login.', 'error', 5000);
    } finally {
      setIsLoading(false);
    }
  };

  // Render based on current step
  if (step === 'loading') {
    return (
      <div className='relative flex min-h-screen items-center justify-center overflow-hidden'>
        {/* Animated background gradient */}
        <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-100 via-white to-cyan-100 dark:from-violet-950 dark:via-slate-900 dark:to-cyan-950' />
        <div className='pointer-events-none absolute inset-0 overflow-hidden'>
          <div className='absolute -top-60 -right-60 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
          <div className='absolute -bottom-60 -left-60 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1.5s]' />
        </div>

        <div className='relative z-10 text-center animate-in fade-in-0 zoom-in-95 duration-500'>
          <div className='relative mx-auto mb-6 inline-flex'>
            <div className='absolute inset-0 animate-pulse-slow rounded-3xl bg-gradient-to-r from-violet-500 to-cyan-500 blur-xl opacity-40' />
            <div className='relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-violet-500 to-cyan-500 shadow-2xl shadow-violet-500/30'>
              <Spinner className='h-10 w-10 text-white' />
            </div>
          </div>
          <p className='text-lg font-medium text-slate-700 dark:text-slate-300'>
            Cargando experiencia...
          </p>
        </div>

        <style>{`
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.6; }
          }
          .animate-pulse-slow {
            animation: pulse-slow 4s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className='relative flex min-h-screen items-center justify-center overflow-hidden p-4'>
        {/* Animated background gradient */}
        <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-violet-100 via-white to-cyan-100 dark:from-violet-950 dark:via-slate-900 dark:to-cyan-950' />
        <div className='pointer-events-none absolute inset-0 overflow-hidden'>
          <div className='absolute -top-60 -right-60 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
          <div className='absolute -bottom-60 -left-60 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1.5s]' />
        </div>

        <div className='relative z-10 w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-500'>
          <div className='relative mx-auto mb-6 inline-flex'>
            <div className='relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-red-500 to-orange-500 shadow-2xl shadow-red-500/30'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='h-10 w-10 text-white'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
                />
              </svg>
            </div>
          </div>

          <div className='rounded-3xl border border-red-200/50 bg-white/60 p-8 shadow-2xl backdrop-blur-xl dark:border-red-800/50 dark:bg-slate-900/60'>
            <h3 className='text-center text-xl font-bold text-slate-900 dark:text-white'>
              Ha ocurrido un error
            </h3>
            <p className='mt-3 text-center text-slate-600 dark:text-slate-400'>
              {error || 'Ha ocurrido un error inesperado'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className='mt-6 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 px-6 py-3 font-semibold text-white shadow-lg shadow-violet-500/30 transition-all hover:scale-[1.02] hover:shadow-xl'
            >
              Reintentar
            </button>
          </div>
        </div>

        <style>{`
          @keyframes pulse-slow {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 0.6; }
          }
          .animate-pulse-slow {
            animation: pulse-slow 4s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  if (step === 'access' && instanceConfig) {
    return (
      <AccessForm
        accessType={instanceConfig.accessType}
        allowedDomains={instanceConfig.allowedDomains}
        mode={accessMode}
        verificationMethod={instanceConfig.verificationMethod}
        onSubmit={handleAccessSubmit}
        onBack={
          accessMode === 'phone' && instanceConfig.accessType !== 'OPEN'
            ? () => setAccessMode('email')
            : undefined
        }
        isLoading={isLoading}
        error={error}
        instanceName={instanceConfig.instanceName}
      />
    );
  }

  if (step === 'otp') {
    return (
      <OtpVerification
        identifier={otpIdentifierType === 'PHONE' ? phone : email}
        identifierType={otpIdentifierType}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        onBack={handleBackFromOtp}
        isLoading={isLoading}
        error={error}
        expiresAt={otpExpiresAt}
      />
    );
  }

  if (step === 'bots') {
    return (
      <BotSelector
        bots={bots}
        onSelectBot={handleSelectBot}
        isLoading={isLoading}
        onLogout={handleLogout}
      />
    );
  }

  if (step === 'chat' && selectedBot) {
    return (
      <div className='fixed inset-x-0 top-[var(--viewport-offset-top)] flex h-[var(--viewport-height)] flex-col overflow-hidden'>
        <ChatWindow
          bot={selectedBot}
          messages={messages}
          onSendMessage={handleSendMessage}
          onNewConversation={
            selectedBot.conversationMode === 'MULTIPLE' ? handleNewConversation : undefined
          }
          onBack={handleBackFromChat}
          isSending={isSending}
          isLoadingMessages={isLoadingMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          hasMoreMessages={hasMoreMessages}
          onLoadMoreMessages={handleLoadMoreMessages}
          conversationMode={selectedBot.conversationMode}
          inputConfig={instanceConfig?.inputConfig}
          onUploadFile={handleUploadFile}
          conversations={conversations}
          onSelectConversation={handleSelectConversation}
          onHideConversation={handleHideConversation}
          currentConversationId={conversationId}
          stickyLabel={stickyLabel}
          hideBack={Boolean(instanceConfig?.webMandatory)}
          limitState={limitState}
          onClearLimit={() => setLimitState(undefined)}
          failedMessageContent={failedMessageContent}
          onFailedMessageCleared={() => setFailedMessageContent(undefined)}
        />
      </div>
    );
  }

  return null;
}
